import { DiagramComponent } from '../../core/DiagramComponent';
import type { DraggableDotConfig, Variables, JXGBoard, JXGElement } from '../../types/interfaces';

/**
 * DraggableDot Component - A dot that can be dragged between bins
 */
class DraggableDot extends DiagramComponent<DraggableDotConfig> {
  dot: JXGElement | null = null;
  originalPosition: [number, number];
  currentBinId: string | null;
  dotId: string;

  constructor(config: DraggableDotConfig, board: JXGBoard, parentState: Variables = {}) {
    super(config, board, parentState);
    this.originalPosition = config.position;
    this.currentBinId = config.binId || null;
    this.dotId = config.dotId;
  }

  render(): JXGElement[] {
    const x = Number(this.evaluateExpression(this.config.position[0]));
    const y = Number(this.evaluateExpression(this.config.position[1]));
    const color = this.config.color || '#3498db';
    const size = this.config.size || 5;

    this.dot = this.board.create('point', [x, y], {
      size: size,
      fillColor: color,
      strokeColor: color,
      strokeWidth: 2,
      fixed: false,
      withLabel: false,
      name: '',
      highlightFillColor: color,
      highlightStrokeColor: '#000',
      showInfobox: false,
      attractors: [], // Will be set by parent
      attractorDistance: 0.5,
      snatchDistance: 1.0,
    });

    this.elements.push(this.dot);

    // Setup drag events
    this.dot.on('drag', () => {
      this._onDrag();
    });

    this.dot.on('up', () => {
      this._onDragEnd();
    });

    return this.elements;
  }

  private _onDrag(): void {
    // Emit drag event for parent to handle
    this._emitEvent('dot-drag', {
      dotId: this.dotId,
      x: (this.dot as any).X(),
      y: (this.dot as any).Y(),
    });
  }

  private _onDragEnd(): void {
    // Emit drop event for parent to handle bin assignment
    this._emitEvent('dot-drop', {
      dotId: this.dotId,
      x: (this.dot as any).X(),
      y: (this.dot as any).Y(),
      previousBinId: this.currentBinId,
    });
  }

  /** Move dot to a specific position */
  moveTo(x: number, y: number): void {
    if (this.dot) {
      (this.dot as any).moveTo([x, y], 200);
    }
  }

  /** Set the current bin this dot belongs to */
  setBinId(binId: string | null): void {
    this.currentBinId = binId;
  }

  /** Get current position */
  getPosition(): [number, number] {
    if (this.dot) {
      return [(this.dot as any).X(), (this.dot as any).Y()];
    }
    return this.originalPosition;
  }

  update(variables: Variables): void {
    this.parentState = { ...this.parentState, ...variables };
  }
}

export default DraggableDot;
